import { useSendContactRequestApi } from '@/api/workspaces';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Calendar, Check, Loader2, Mail, Phone } from 'lucide-react';
import { type FC, useState } from 'react';
import { toast } from 'sonner';
import { cn } from '@/utils/classUtils';

const CAL_LINK_FALLBACK = 'https://cal.com/alexis-qalico/30min';

type ContactOption = 'callback' | 'email' | 'meeting';

interface ContactUsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  topContent?: React.ReactNode;
}

export const ContactUsModal: FC<ContactUsModalProps> = ({
  open,
  onOpenChange,
  title = 'Talk to our team',
  description = "Choose how you'd like to connect with us.",
  topContent,
}) => {
  const [selectedOption, setSelectedOption] = useState<ContactOption | null>(
    null
  );
  const [phoneNumber, setPhoneNumber] = useState('');
  const [submitted, setSubmitted] = useState<ContactOption | null>(null);
  const { mutate: sendContactRequest, isPending } = useSendContactRequestApi();

  const handleReset = () => {
    setSelectedOption(null);
    setPhoneNumber('');
    setSubmitted(null);
  };

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      handleReset();
    }
    onOpenChange(value);
  };

  const handleSendCallback = () => {
    if (!phoneNumber.trim()) return;
    sendContactRequest(
      { type: 'callback', phoneNumber: phoneNumber.trim() },
      {
        onSuccess: () => {
          setSubmitted('callback');
          toast.success("We'll call you back shortly.");
        },
        onError: () => {
          toast.error('Something went wrong. Please try again.');
        },
      }
    );
  };

  const handleSendEmail = () => {
    sendContactRequest(
      { type: 'email' },
      {
        onSuccess: () => {
          setSubmitted('email');
          toast.success("We'll get back to you within one business day.");
        },
        onError: () => {
          toast.error('Something went wrong. Please try again.');
        },
      }
    );
  };

  const handleBookMeeting = () => {
    sendContactRequest(
      { type: 'meeting' },
      {
        onSuccess: data => {
          window.open(data.calUrl || CAL_LINK_FALLBACK, '_blank');
          handleOpenChange(false);
        },
        onError: () => {
          window.open(CAL_LINK_FALLBACK, '_blank');
          handleOpenChange(false);
        },
      }
    );
  };

  const options: {
    key: ContactOption;
    icon: typeof Phone;
    title: string;
    description: string;
  }[] = [
    {
      key: 'callback',
      icon: Phone,
      title: 'Request a callback',
      description: "Leave your number, we'll reach out shortly.",
    },
    {
      key: 'email',
      icon: Mail,
      title: 'Get a reply by email',
      description: "We'll get back to you within one business day.",
    },
    {
      key: 'meeting',
      icon: Calendar,
      title: 'Book a meeting',
      description: 'Pick a 30-minute slot that works for you.',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn('sm:max-w-[540px]', topContent && 'sm:max-w-[680px]')}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {topContent && <div className="mb-1">{topContent}</div>}

        <div className="flex flex-col gap-3">
          {options.map(option => {
            const isSelected = selectedOption === option.key;
            const isSubmitted = submitted === option.key;

            return (
              <div key={option.key}>
                <button
                  type="button"
                  disabled={isPending || (submitted !== null && !isSubmitted)}
                  onClick={() => {
                    if (isSubmitted) return;
                    if (option.key === 'email') {
                      handleSendEmail();
                    } else if (option.key === 'meeting') {
                      handleBookMeeting();
                    } else {
                      setSelectedOption(isSelected ? null : option.key);
                    }
                  }}
                  className={cn(
                    'flex w-full items-start gap-4 rounded-lg border p-4 text-left transition-colors',
                    isSelected
                      ? 'border-neutral-900 bg-neutral-50'
                      : 'border-border hover:border-neutral-400 hover:bg-neutral-50',
                    isSubmitted && 'border-green-500 bg-green-50',
                    isPending && 'opacity-70',
                    submitted !== null &&
                      !isSubmitted &&
                      'opacity-40 cursor-default'
                  )}
                >
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-neutral-100">
                    {isSubmitted ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <option.icon className="h-4 w-4 text-neutral-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{option.title}</p>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {isSubmitted
                        ? option.key === 'callback'
                          ? "Got it! We'll call you back shortly."
                          : "Got it! We'll email you within one business day."
                        : option.description}
                    </p>
                  </div>
                </button>

                {/* Inline phone input for callback */}
                {option.key === 'callback' && isSelected && !isSubmitted && (
                  <div className="mt-2 flex items-center gap-2 pl-[52px]">
                    <Input
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      value={phoneNumber}
                      onChange={e => setPhoneNumber(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      onClick={handleSendCallback}
                      disabled={!phoneNumber.trim() || isPending}
                    >
                      {isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Submit'
                      )}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
